/**
 * WorkspaceAgentsTab Component
 *
 * Tab for managing workspace agents with 3 visibility levels:
 * 1. GLOBAL - TD agents visible to all workspaces
 * 2. WORKSPACE - Agents visible to all workspace members
 * 3. GROUP - Agents visible only to specific group members
 */

import React, { useState, useEffect } from 'react';
import { Workspace, WorkspaceGroup } from '../../../types/workspace';
import { Agent } from '../../../types/agent';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { Bot, Globe, Building2, Users, Eye, EyeOff } from 'lucide-react';
import { listAgentsByWorkspace } from '../../../services/agentService';

interface WorkspaceAgentsTabProps {
  workspace: Workspace;
}

interface AgentsByLevel {
  global: Agent[];
  workspace: Agent[];
  group: { [groupId: number]: Agent[] };
}

export const WorkspaceAgentsTab: React.FC<WorkspaceAgentsTabProps> = ({ workspace }) => {
  const { loadWorkspaceGroups } = useWorkspace();
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [agents, setAgents] = useState<AgentsByLevel>({
    global: [],
    workspace: [],
    group: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load groups and agents
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Load groups
        const groupsData = await loadWorkspaceGroups(workspace.id);
        setGroups(groupsData);

        // Load ALL agents for this workspace (paginated, max 100 per request)
        const allAgents: Agent[] = [];
        let skip = 0;
        const limit = 100; // Backend max limit
        let hasMore = true;

        while (hasMore) {
          const agentsResponse = await listAgentsByWorkspace(workspace.id, {
            deployed_only: false,
            skip,
            limit,
          });

          allAgents.push(...agentsResponse.data);

          // Check if there are more agents to load
          hasMore = agentsResponse.data.length === limit;
          skip += limit;
        }

        // Organize agents by visibility level
        const organized: AgentsByLevel = {
          global: [],
          workspace: [],
          group: {},
        };

        allAgents.forEach((agent) => {
          if (agent.visibility_level === 'global') {
            organized.global.push(agent);
          } else if (agent.visibility_level === 'workspace') {
            organized.workspace.push(agent);
          } else if (agent.visibility_level === 'group') {
            // Legacy model: single group via created_by_group_id
            if (agent.created_by_group_id) {
              if (!organized.group[agent.created_by_group_id]) {
                organized.group[agent.created_by_group_id] = [];
              }
              organized.group[agent.created_by_group_id].push(agent);
            } else if (agent.visible_group_ids && agent.visible_group_ids.length > 0) {
              // N:N model: agent published to multiple groups
              agent.visible_group_ids.forEach((groupId) => {
                if (!organized.group[groupId]) {
                  organized.group[groupId] = [];
                }
                organized.group[groupId].push(agent);
              });
            }
          }
        });

        setAgents(organized);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha ao carregar dados');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [workspace.id, loadWorkspaceGroups]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F4CC04]"></div>
      </div>
    );
  }

  // Count total agents per group (including workspace-level agents)
  const getGroupAgentCount = (groupId: number): number => {
    const groupSpecificAgents = agents.group[groupId]?.length || 0;
    const workspaceAgents = agents.workspace.length;
    const globalAgents = agents.global.length;
    return groupSpecificAgents + workspaceAgents + globalAgents;
  };

  return (
    <div className="space-y-8">
      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
      )}

      {/* GLOBAL AGENTS (TD Only) */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-500/20 rounded-lg">
              <Globe className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Agentes Globais (TD)</h3>
              <p className="text-sm text-[#888888]">
                Visíveis para todos os workspaces ({agents.global.length} agentes)
              </p>
            </div>
          </div>
        </div>

        {agents.global.length === 0 ? (
          <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-8 text-center">
            <Globe className="w-12 h-12 text-[#888888] mx-auto mb-3" />
            <p className="text-[#888888]">Nenhum agente global disponível</p>
            <p className="text-xs text-[#666666] mt-1">Apenas TD pode criar agentes globais</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {agents.global.map((agent) => (
              <AgentCard key={agent.id} agent={agent} level="global" />
            ))}
          </div>
        )}
      </section>

      {/* WORKSPACE AGENTS (All Members) */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-[#F4CC04]/20 rounded-lg">
              <Building2 className="w-5 h-5 text-[#F4CC04]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Agentes do Workspace</h3>
              <p className="text-sm text-[#888888]">
                Visíveis para todos os membros da empresa ({agents.workspace.length} agentes)
              </p>
            </div>
          </div>
        </div>

        {agents.workspace.length === 0 ? (
          <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-8 text-center">
            <Building2 className="w-12 h-12 text-[#888888] mx-auto mb-3" />
            <p className="text-[#888888]">Nenhum agente de workspace ainda</p>
            <p className="text-xs text-[#666666] mt-1">Crie agentes com visibilidade "Workspace" na tela de Agentes</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {agents.workspace.map((agent) => (
              <AgentCard key={agent.id} agent={agent} level="workspace" />
            ))}
          </div>
        )}
      </section>

      {/* GROUP AGENTS (Group-Specific) */}
      <section>
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-500/20 rounded-lg">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Agentes por Grupo</h3>
              <p className="text-sm text-[#888888]">
                Agentes privados de cada grupo ({groups.length} grupos)
              </p>
            </div>
          </div>
        </div>

        {groups.length === 0 ? (
          <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-8 text-center">
            <Users className="w-12 h-12 text-[#888888] mx-auto mb-3" />
            <p className="text-[#888888]">Nenhum grupo criado ainda</p>
            <p className="text-xs text-[#666666] mt-1">Crie grupos na aba "Grupos" para associar agentes</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <GroupAgentsSection
                key={group.id}
                group={group}
                agents={agents.group[group.id] || []}
                totalVisibleAgents={getGroupAgentCount(group.id)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

// Agent Card Component
interface AgentCardProps {
  agent: Agent;
  level: 'global' | 'workspace' | 'group';
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, level }) => {
  const getLevelBadge = () => {
    switch (level) {
      case 'global':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-full font-medium">
            <Globe className="w-3 h-3" />
            Global
          </span>
        );
      case 'workspace':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[#F4CC04]/20 text-[#F4CC04] rounded-full font-medium">
            <Building2 className="w-3 h-3" />
            Workspace
          </span>
        );
      case 'group':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full font-medium">
            <Users className="w-3 h-3" />
            Grupo
          </span>
        );
    }
  };

  return (
    <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-4 hover:border-[#444444] transition-all">
      {/* Header with title and status icon */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center justify-center w-10 h-10 bg-[#F4CC04]/20 rounded-lg flex-shrink-0">
            <Bot className="w-5 h-5 text-[#F4CC04]" />
          </div>
          <div className="flex-1 min-w-0">
            <h4
              className="font-semibold text-white truncate mb-1"
              title={agent.name}
            >
              {agent.name}
            </h4>
            {getLevelBadge()}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {agent.deploy_status ? (
            <span className="p-1 text-green-400" title="Implantado">
              <Eye className="w-4 h-4" />
            </span>
          ) : (
            <span className="p-1 text-[#888888]" title="Não implantado">
              <EyeOff className="w-4 h-4" />
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      {agent.description && (
        <p className="text-sm text-[#888888] mb-3 line-clamp-2">{agent.description}</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-[#888888] border-t border-[#333333] pt-3">
        <span className="truncate">Criado em {new Date(agent.created_at).toLocaleDateString('pt-BR')}</span>
        <span className="font-medium text-white ml-2 flex-shrink-0">{agent.model}</span>
      </div>
    </div>
  );
};

// Group Agents Section Component
interface GroupAgentsSectionProps {
  group: WorkspaceGroup;
  agents: Agent[];
  totalVisibleAgents: number;
}

const GroupAgentsSection: React.FC<GroupAgentsSectionProps> = ({ group, agents, totalVisibleAgents }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg overflow-hidden">
      {/* Group Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-[#222222] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-blue-500/20 rounded-lg">
            <Users className="w-5 h-5 text-blue-400" />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-white">{group.name}</h4>
            <p className="text-xs text-[#888888]">
              {agents.length} agentes privados • {totalVisibleAgents} visíveis no total
            </p>
          </div>
        </div>

        <svg
          className={`w-5 h-5 text-[#888888] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Group Agents List */}
      {isExpanded && (
        <div className="border-t border-[#333333] p-4">
          {agents.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-[#888888] mx-auto mb-2" />
              <p className="text-[#888888] text-sm">Nenhum agente privado neste grupo</p>
              <p className="text-xs text-[#666666] mt-1">
                Membros veem {totalVisibleAgents} agentes (workspace + global)
              </p>
              <p className="text-xs text-[#666666] mt-2">
                Crie agentes com visibilidade "Grupo" e selecione este grupo na tela de Agentes
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {agents.map((agent) => (
                <AgentCard key={agent.id} agent={agent} level="group" />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkspaceAgentsTab;
